import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { AuthProvider } from './AuthContext';

const maskSecret = (value) => {
  if (typeof value !== 'string') return value;
  const text = value.trim();
  if (!text) return value;
  if (text.length <= 8) return '***';
  return `${text.slice(0, 4)}...${text.slice(-4)}`;
};

const shouldRedactKey = (key = '') => {
  const normalized = String(key || '').toLowerCase();
  return /(token|jwt|authorization|secret|password|cookie|api[_-]?key|access[_-]?token|refresh[_-]?token|bearer|email|phone|resume|cv|credential|code)/i.test(normalized);
};

const looksSensitiveString = (value, key = '') => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (shouldRedactKey(key)) return true;
  if (trimmed.startsWith('Bearer ') || trimmed.startsWith('eyJ')) return true;
  if (/jwt|token|secret|password|cookie|authorization|api[_-]?key|credential/i.test(trimmed)) return true;
  if (/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/.test(trimmed)) return true;
  if (/(?:\+?\d[\d\s().-]{8,}\d)/.test(trimmed)) return true;
  if (trimmed.length > 180) return true;
  return false;
};

const sanitizeForUI = (value, key = '') => {
  if (Array.isArray(value)) {
    return value.map((item, index) => sanitizeForUI(item, `${key}[${index}]`));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([entryKey, entryValue]) => [
        entryKey,
        shouldRedactKey(entryKey) || looksSensitiveString(String(entryValue), entryKey)
          ? maskSecret(String(entryValue))
          : sanitizeForUI(entryValue, entryKey)
      ])
    );
  }

  if (typeof value === 'string') {
    if (looksSensitiveString(value, key)) return maskSecret(value);
    return value;
  }

  return value;
};

const originalConsole = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
  debug: console.debug.bind(console),
};

const safeConsoleMethod = (method) => (...args) => {
  const redactedArgs = args.map((arg, index) => {
    const previousArg = typeof args[index - 1] === 'string' ? args[index - 1] : '';
    if (shouldRedactKey(previousArg)) {
      return maskSecret(typeof arg === 'string' ? arg : JSON.stringify(arg));
    }
    return sanitizeForUI(arg, previousArg);
  });

  originalConsole[method](...redactedArgs);
};

console.log = safeConsoleMethod('log');
console.info = safeConsoleMethod('info');
console.warn = safeConsoleMethod('warn');
console.error = safeConsoleMethod('error');
console.debug = safeConsoleMethod('debug');

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <App />
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>
);
